import { ThemeProvider } from '@emotion/react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { createTheme, styled } from '@mui/material/styles';
import { getAllBackstage } from 'APIs/BackstageApi';
import { useAppSelector } from 'app/hooks';
import { NormalButton } from 'components/atoms/Button';
import Loader from 'components/atoms/loader';
import NoPostsCard from 'components/molecules/Card/NoPostsCard';
import ProfileOtherPostCardWithData from 'components/molecules/Card/ProfileOtherPostCardWithData';
import ProfileOwnPostCard from 'components/molecules/Card/ProfileOwnPostCard';
import { Indicator } from 'components/organisms/Header/OtherProfileHeader/styled';
import BackstageSidebar from 'components/organisms/Sidebar/BackstageSidebar';
import { selectAuth } from 'features/auth/authSlice';
import Backstage from 'interfaces/Backstage.interface';
import { useEffect, useState } from 'react';
import { TabsWrapper } from '../DMessage/DMessage.styles';
import SubscribedTo from './SubscribedTo';
import TransactionsBody from './TransactionsBody';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const Body = styled(Grid)(({ theme }) => ({
  // position: "absolute",
  display: 'flex',
  padding: '130px 130px',
  [theme.breakpoints.down('lg')]: {
    padding: '40px 40px',
  },
  minWidth: '60vw',
  gap: '6px',
  [theme.breakpoints.down('md')]: {
    padding: '20px 20px',
  },
}));

const BackstageBody = () => {
  const [data, setData] = useState<Backstage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>('feed');
  const authState = useAppSelector(selectAuth);
  const fetchData = async () => {
    setLoading(true);
    const data = await getAllBackstage();
    if (data) {
      setData(data);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const addPost = (post: Backstage) => {
    setData(posts => [{ ...post, likedBy: [], comments: [] }, ...posts]);
  };

  return (
    <>
      <ThemeProvider theme={colortheme}>
        <Body
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { md: '20px', xs: '0px' },
            paddingTop: '0px',
            marginTop: { md: '60px', xs: '0px' },
            flexWrap: { md: 'nowrap', xs: 'wrap' },
            overflowX: 'hidden',
          }}
        >
          <BackstageSidebar
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '100px',
            }}
          >
            <TabsWrapper noBackground>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { lg: 'space-between', xs: 'center' },
                  flexWrap: { xs: 'wrap', lg: 'nowrap' },
                }}
              >
                <NormalButton
                  onClick={() => setCurrentTab('feed')}
                  color={currentTab === 'feed' ? 'yellow' : 'transparent'}
                  style={{ width: '100%' }}
                >
                  Feed
                </NormalButton>
                <NormalButton
                  onClick={() => setCurrentTab('library')}
                  color={currentTab === 'library' ? 'yellow' : 'transparent'}
                  style={{ width: '100%' }}
                >
                  Library <Indicator>2</Indicator>
                </NormalButton>
                <NormalButton
                  onClick={() => setCurrentTab('subscription')}
                  color={
                    currentTab === 'subscription' ? 'yellow' : 'transparent'
                  }
                  style={{ width: '100%' }}
                >
                  My Subscriptions{' '}
                  <Indicator sx={{ flexShrink: 0 }}>
                    {authState.subscribedTo?.length}
                  </Indicator>
                </NormalButton>
                <NormalButton
                  onClick={() => setCurrentTab('dashboard')}
                  color={currentTab === 'dashboard' ? 'yellow' : 'transparent'}
                  style={{ width: '100%' }}
                  isDisabled={!authState.isCreator}
                >
                  Dashboard
                </NormalButton>
              </Box>
            </TabsWrapper>
            <Body
              container
              style={{
                maxWidth: '1300px',
                background: 'rgba(255,255,255,0.01)',
                borderRadius: '20px',
                paddingTop: '40px',
                margin: '0px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              {currentTab === 'feed' ? (
                <>
                  {' '}
                  <ProfileOwnPostCard
                    fetchData={fetchData}
                    backstagePosts={data}
                    addPost={addPost}
                  />
                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        margin: '80px 0px',
                      }}
                    >
                      <Loader />
                    </Box>
                  ) : data && data.length > 0 ? (
                    data.map(backstage => (
                      <ProfileOtherPostCardWithData
                        key={backstage.id}
                        data={backstage}
                      />
                    ))
                  ) : (
                    <NoPostsCard />
                  )}{' '}
                </>
              ) : currentTab === 'subscription' ? (
                <SubscribedTo />
              ) : (
                <TransactionsBody />
              )}
            </Body>
          </Box>
        </Body>
      </ThemeProvider>
    </>
  );
};

export default BackstageBody;
